import { useEffect, lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Routes, Route, useLocation } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import { useThemeStore } from './stores/themeStore'
import { getThemeUrl } from './themes/registry'
import { routeStateKey } from './lib/boardUrls'
import { Toaster } from './components/ui/toaster'

// Layouts (kept eager – used on most routes)
import { MainLayout } from './components/layout/MainLayout'

// Auth route wrappers (small, eager)
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { AdminRoute } from './components/auth/AdminRoute'
import { GanttRedirectPage } from './components/gantt/GanttRedirect'

// Lazy-loaded pages
const LoginPage = lazy(() => import('./components/auth/LoginForm').then((m) => ({ default: m.LoginPage })))
const RegisterPage = lazy(() => import('./components/auth/RegisterForm').then((m) => ({ default: m.RegisterPage })))
const ForgotPasswordPage = lazy(() => import('./components/auth/ForgotPassword').then((m) => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPassword').then((m) => ({ default: m.ResetPasswordPage })))
const OAuthCallbackPage = lazy(() => import('./components/auth/OAuthCallback').then((m) => ({ default: m.OAuthCallbackPage })))
const PendingApprovalPage = lazy(() => import('./components/auth/PendingApproval').then((m) => ({ default: m.PendingApprovalPage })))
const VerifyEmailPage = lazy(() => import('./components/auth/VerifyEmail').then((m) => ({ default: m.VerifyEmailPage })))
const CliActivatePage = lazy(() => import('./components/auth/CliActivate').then((m) => ({ default: m.CliActivatePage })))
const CliGuidePage = lazy(() => import('./components/cli/CliGuide').then((m) => ({ default: m.CliGuidePage })))
const BoardListPage = lazy(() => import('./components/board/BoardList').then((m) => ({ default: m.BoardListPage })))
const BoardPage = lazy(() => import('./components/board/Board').then((m) => ({ default: m.BoardPage })))
const MyTicketsPage = lazy(() => import('./components/ticket/MyTickets').then((m) => ({ default: m.MyTicketsPage })))
const BoardSettingsPage = lazy(() => import('./components/board/BoardSettings').then((m) => ({ default: m.BoardSettingsPage })))
const ProfilePage = lazy(() => import('./components/user/Profile').then((m) => ({ default: m.ProfilePage })))
const AdminDashboardPage = lazy(() => import('./components/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboardPage })))
const GanttPage = lazy(() => import('./components/gantt/GanttPage').then((m) => ({ default: m.GanttPage })))
const UserManagementPage = lazy(() => import('./components/admin/UserManagement').then((m) => ({ default: m.UserManagementPage })))
const AppearancePage = lazy(() => import('./components/appearance/AppearancePage').then((m) => ({ default: m.AppearancePage })))
const NotFoundPage = lazy(() => import('./components/NotFoundPage').then((m) => ({ default: m.NotFoundPage })))

function App() {
  const { t } = useTranslation()
  const location = useLocation()
  const initializeAuth = useAuthStore((state) => state.initializeAuth)
  const theme = useThemeStore((state) => state.theme)
  const colorTheme = useThemeStore((state) => state.colorTheme)

  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  useEffect(() => {
    const linkId = 'theme-color'
    const existing = document.getElementById(linkId) as HTMLLinkElement | null
    const url = getThemeUrl(colorTheme)
    if (!url) {
      if (existing) existing.remove()
      return
    }
    if (existing) {
      existing.href = url
      return
    }
    const link = document.createElement('link')
    link.id = linkId
    link.rel = 'stylesheet'
    link.href = url
    document.head.appendChild(link)
  }, [colorTheme])

  useEffect(() => {
    document.body.style.pointerEvents = ''
  }, [])

  return (
    <>
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center">{t('common.loading')}</div>}>
        <Routes key={routeStateKey(location.pathname)}>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/auth/pending-approval" element={<PendingApprovalPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/" element={<BoardListPage />} />
            <Route path="/boards" element={<BoardListPage />} />
            <Route path="/boards/:id/settings" element={<BoardSettingsPage />} />
            <Route path="/boards/:id/:ticketKey" element={<BoardPage />} />
            <Route path="/boards/:id" element={<BoardPage />} />
            <Route path="/tickets" element={<MyTicketsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/cli" element={<CliGuidePage />} />
            <Route path="/cli/activate" element={<CliActivatePage />} />
            <Route path="/appearance" element={<AppearancePage />} />
            <Route path="/gantt" element={<GanttPage />} />
            <Route path="/admin/gantt" element={<GanttRedirectPage />} />

            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/users" element={<UserManagementPage />} />
            </Route>
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      <Toaster />
    </>
  )
}

export default App
